import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import StatusBadge from "../../components/StatusBadge";

export default function AllDeals({ scope = "broker_admin" }) {
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/rentals");
      setRentals(data);
    })();
  }, []);

  // Fetch saved draft agreements
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agreements/drafts");
        setDrafts(data.drafts || []);
      } catch (error) {
        console.error('Error fetching drafts:', error);
        setDrafts([]);
      }
    })();
  }, []);

  // Delete a draft
  const handleDeleteDraft = async (draftId, draftName) => {
    const confirmed = window.confirm(
      `⚠️ Are you sure you want to delete "${draftName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/agreements/drafts/${draftId}`);
      setDrafts(drafts.filter(d => d.draft_id !== draftId));
      alert(`✅ Draft "${draftName}" deleted successfully`);
    } catch (error) {
      alert(`Error deleting draft: ${error.response?.data?.detail || error.message}`);
    }
  };

  const agents = useMemo(() => {
    const map = new Map();
    rentals.forEach(r => { if (r.agent) map.set(r.agent_id, r.agent.name); });
    return [...map.entries()];
  }, [rentals]);

  const filtered = rentals.filter(r => {
    if (agentFilter !== "all" && r.agent_id !== agentFilter) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (q && !`${r.property_address}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="all-deals-page">
      <div>
        <h1 className="text-2xl font-bold font-display">{scope === "agent" ? "My Deals" : "All Deals"}</h1>
        <p className="text-sm text-gray-500 mt-1">{scope === "agent" ? "Every rental you've created." : "Every rental created by your team."}</p>
      </div>

      {/* SAVED DRAFT AGREEMENTS SECTION */}
      {drafts.length > 0 ? (
        <div className="card-base overflow-hidden border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-50 to-transparent">
          <div className="px-5 py-4 bg-gradient-to-r from-orange-500 to-orange-600 border-b border-orange-600">
            <h2 className="text-lg font-semibold text-white">📋 Saved Draft Agreements ({drafts.length})</h2>
            <p className="text-sm text-orange-100 mt-1">Continue editing agreements you started but haven't submitted yet</p>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] text-orange-700 uppercase tracking-wider bg-orange-50 font-bold">
              <tr>
                <th className="text-left px-5 py-3">Agreement Name</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-5 py-3">Property</th>
                <th className="text-center px-5 py-3">Progress</th>
                <th className="text-left px-5 py-3">Last Updated</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => (
                <tr key={draft.draft_id} className="border-t border-orange-100 hover:bg-orange-50 transition-colors">
                  <td className="px-5 py-3 font-medium text-gray-900">{draft.agreement_name}</td>
                  <td className="px-5 py-3 text-gray-600">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {draft.agreement_type === 'e_agreement' ? 'E-Agreement' : draft.agreement_type === 'e_stamp' ? 'E-Stamp' : 'Traditional'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600 text-sm">
                    {draft.property_type ? draft.property_type.charAt(0).toUpperCase() + draft.property_type.slice(1) : '—'}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      Step {draft.current_step}/8
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600 text-sm">{formatDate(draft.updated_at)}</td>
                  <td className="px-5 py-3 text-right space-x-2 flex justify-end">
                    <button
                      onClick={() => navigate('/app/agreement-builder', { state: { resumeDraftId: draft.draft_id } })}
                      className="inline-block px-3 py-1 rounded bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors shadow-md"
                    >
                      Continue Editing →
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(draft.draft_id, draft.agreement_name)}
                      className="inline-block px-3 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs font-semibold transition-colors shadow-md"
                      title="Delete this draft (cannot be undone)"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-base overflow-hidden border-l-4 border-l-gray-300">
          <div className="px-5 py-8 text-center">
            <p className="text-gray-600 text-sm mb-4">📝 No saved draft agreements yet</p>
            <p className="text-gray-500 text-xs mb-6">Save agreements as you create them and continue editing later with no expiration</p>
            <button
              onClick={() => navigate('/app/agreement-builder')}
              className="inline-block px-4 py-2 rounded bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
            >
              + Start New Agreement
            </button>
          </div>
        </div>
      )}

      <div className="card-base p-3 flex flex-wrap gap-2 items-center">
        {scope !== "agent" && (
          <select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="all">All Agents</option>
            {agents.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="all">All Statuses</option>
          {["agreement_pending","payment_pending","active","checkout_pending","settlement_pending","dispute","completed"].map(s => <option key={s}>{s}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search property…" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[200px]" />
      </div>

      <div className="card-base overflow-hidden">
        {filtered.length === 0 ? <div className="p-10 text-center text-gray-500 text-sm">No deals yet.</div> : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50">
              <tr><th className="text-left px-5 py-3">Property</th>{scope!=="agent"&&<th className="text-left px-5 py-3">Agent</th>}<th className="text-left px-5 py-3">Tenant</th><th className="text-right px-5 py-3">Deposit</th><th className="text-left px-5 py-3">Created</th><th className="text-left px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium">{r.property_address}</td>
                  {scope!=="agent" && <td className="px-5 py-3 text-gray-600">{r.agent?.name || "—"}</td>}
                  <td className="px-5 py-3 text-gray-600">{r.tenants_resolved?.[0]?.user?.name || "—"}</td>
                  <td className="px-5 py-3 text-right font-medium">{formatINR(r.deposit_amount)}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(r.created_at)}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-right"><Link to={`/app/rental/${r.id}`} className="text-[#F97316]">View →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
